// ─────────────────────────────────────────────────────────────────────────────
// Helpers compartidos de "Brillamos Juntos".
//   - resolveVisibility (REQ-113): calcula los toggles de visibilidad EFECTIVOS de la campaña.
//     Regla de negocio: al CIERRE de la campaña (estado "Finalizada") todo se revela; durante la
//     campaña se respetan los toggles (modo minimal Europa oculta remitente/texto/hábito).
//     Los PUNTOS nunca son visibles (no existen en los DTOs públicos — regla de oro).
//   - formatRelative: tiempo relativo localizado para el feed.
// ─────────────────────────────────────────────────────────────────────────────
import type { CampaignDto } from '@/api/recognition';
import { getInitials } from '@/utils/strings';

export interface EffectiveVisibility {
  mostrarRemitente: boolean;
  mostrarFundamental: boolean;
  mostrarTexto: boolean;
  mostrarHabito: boolean;
  /** true cuando la campaña ya cerró y todo quedó revelado. */
  revelado: boolean;
}

/**
 * Toggles de visibilidad efectivos de una campaña para el feed de Brillamos.
 * - Sin campaña (null): todo visible (reconocimiento diario).
 * - Campaña Finalizada: todo revelado (revelado=true) sin importar los toggles.
 * - Campaña Activa/Borrador: se respeta `visibilidad` (modo minimal = campos ocultos).
 */
export function resolveVisibility(campaign: CampaignDto | null | undefined): EffectiveVisibility {
  if (!campaign) {
    return { mostrarRemitente: true, mostrarFundamental: true, mostrarTexto: true, mostrarHabito: true, revelado: false };
  }
  if (campaign.estado === 'Finalizada') {
    return { mostrarRemitente: true, mostrarFundamental: true, mostrarTexto: true, mostrarHabito: true, revelado: true };
  }
  const v = campaign.visibilidad;
  return {
    mostrarRemitente: v.mostrarRemitente,
    mostrarFundamental: v.mostrarFundamental,
    mostrarTexto: v.mostrarTexto,
    mostrarHabito: v.mostrarHabito,
    revelado: false,
  };
}

/** Días restantes (>=0) hasta la fecha de fin de una campaña; null si no hay fecha. */
export function daysLeft(campaign: CampaignDto | null | undefined): number | null {
  if (!campaign?.fechaFin) return null;
  const end = new Date(campaign.fechaFin).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86_400_000));
}

/** Tiempo relativo localizado (hace un momento / hace Nh / ayer / hace N días). */
export function formatRelative(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const diff = Date.now() - ts;
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (hours < 1) return t('time.moment');
  if (hours < 24) return t('time.hours', { n: hours });
  if (days === 1) return t('time.yesterday');
  return t('time.days', { n: days });
}

// ─── Avatares (F5 — Brillamos Juntos / Nominar) ─────────────────────────────
// El color del avatar de iniciales se unificó el 2026-09-03 en `@/utils/avatarPalette` /
// `<InitialsAvatar>` — la paleta propia de este archivo (`var(--cp1..6)`, hash de 31) se retiró.
// `initialsFor` era la otra implementación de "obtener iniciales de un nombre". Se conserva el
// NOMBRE como alias para no tocar a `NominarView`/`InnovacionView`, pero la lógica es única
// (`@/utils/strings`).
export { getInitials as initialsFor };
